import React, { memo } from "react";
import {
  Box, Flex, Text, Popover, PopoverTrigger, PopoverContent, Portal, Tooltip,
} from "@chakra-ui/react";
import { PlusCircleIcon, PencilIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import { ReactComponent as MicIcon } from "./icons/mic.svg";
import { ReactComponent as TypeIcon } from "./icons/type.svg";
import { ANSWER_MODES, MODE_TOGGLE } from "./answerModes";
import AddMenu from "./AddMenu";
import { a, PALETTES } from "../appColors";

/**
 * The row along the bottom of an answer box.
 *
 * The same component serves both modes: in type mode it sits flat on the
 * card (Figma 194:67), in pen mode it floats as a pill over the canvas
 * (215:187). Only the wrapper differs, so the controls cannot drift apart.
 */
const AnswerActionBar = ({
  mode,
  onToggleMode,
  figures,
  addedFigureIds,
  onUpload,
  onInsertFormula,
  onInsertFigure,
  onSubmit,
  /** Back out of an edit without changing the answer, when there is one to
   *  go back to. */
  onCancel,
  canSubmit,
  isSubmitting,
  onDictate,
  isDictating,
  dictationSupported = true,
  readOnly,
  isDark,
}) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;
  const toggle = MODE_TOGGLE[mode];
  const floating = mode === ANSWER_MODES.PEN;
  const ToggleIcon = mode === ANSWER_MODES.TYPE ? PencilIcon : TypeIcon;

  if (readOnly) return null;

  return (
    <Flex
      align="center"
      justify="space-between"
      gap="12px"
      // In pen mode the bar lifts off the canvas so the drawing runs behind
      // it; in type mode it is part of the card.
      {...(floating
        ? {
            position: "absolute", bottom: "11px", left: "50%",
            transform: "translateX(-50%)", width: "min(571px, calc(100% - 24px))",
            bg: p.surfaceSubtle, borderRadius: "200px", p: "8px", zIndex: 2,
            boxShadow: "0 2px 10px -4px rgba(0,0,0,0.18)",
          }
        : { px: "19px", pb: "14px", pt: "10px" })}
    >
      <Flex align="center" gap="8px" minW={0}>
        <Popover placement="top-start" gutter={10} isLazy>
          {({ onClose }) => (
          <>
          <PopoverTrigger>
            <Box
              as="button"
              type="button"
              aria-label="Add to your answer"
              color={a.text}
              lineHeight="0"
              borderRadius="full"
              cursor="pointer"
              _hover={{ color: a.accent }}
              _focusVisible={{ outline: "2px solid", outlineColor: a.text, outlineOffset: "2px" }}
            >
              <PlusCircleIcon style={{ width: 24, height: 24 }} />
            </Box>
          </PopoverTrigger>
          {/* Portalled: the box clips its own overflow, and the menu is
              taller than the bar it hangs off. */}
          <Portal>
            <PopoverContent
              w="auto"
              bg="transparent"
              border="none"
              boxShadow="none"
              _focusVisible={{ outline: "none" }}
            >
              <AddMenu
                figures={figures}
                addedFigureIds={addedFigureIds}
                onUpload={onUpload}
                // The formula field opens in the box, right where the menu
                // hangs, so the menu gets out of its way.
                onInsertFormula={
                  onInsertFormula
                    ? () => {
                        onClose();
                        onInsertFormula();
                      }
                    : undefined
                }
                onInsertFigure={onInsertFigure}
              />
            </PopoverContent>
          </Portal>
          </>
          )}
        </Popover>

        <Flex
          as="button"
          type="button"
          align="center"
          justify="center"
          gap="4px"
          h="22px"
          px="8px"
          borderRadius="200px"
          bg={floating ? p.surface : p.surfaceSubtle}
          border="0.5px solid"
          borderColor={a.edge}
          color={a.text}
          fontSize="11px"
          lineHeight="16px"
          whiteSpace="nowrap"
          cursor="pointer"
          transition="background 0.12s"
          _hover={{ bg: a.hover }}
          onClick={onToggleMode}
          aria-label={toggle.label}
        >
          <Text as="span">{toggle.label}</Text>
          <ToggleIcon width="14px" height="14px" aria-hidden focusable="false" />
        </Flex>
      </Flex>

      <Flex align="center" gap="9px" flexShrink={0}>
        {onCancel && (
          <Box
            as="button"
            type="button"
            aria-label="Cancel editing"
            fontSize="11px"
            color={a.textSub}
            px="8px"
            h="22px"
            borderRadius="200px"
            cursor="pointer"
            _hover={{ bg: a.hover, color: a.text }}
            onClick={onCancel}
          >
            Cancel
          </Box>
        )}

        <Tooltip
          label={
            !dictationSupported
              ? "Dictation needs Chrome, Edge or Safari"
              : isDictating
              ? "Stop dictating"
              : "Dictate your answer"
          }
          placement="top"
          hasArrow
          fontSize="11px"
        >
          <Box
            as="button"
            type="button"
            aria-label={isDictating ? "Stop dictating" : "Dictate your answer"}
            aria-pressed={isDictating}
            disabled={!dictationSupported}
            color={isDictating ? a.accent : a.text}
            opacity={!dictationSupported ? 0.35 : isDictating ? 1 : 0.76}
            lineHeight="0"
            cursor={dictationSupported ? "pointer" : "not-allowed"}
            _hover={dictationSupported ? { opacity: 1, color: a.accent } : {}}
            onClick={dictationSupported ? onDictate : undefined}
            // A quiet pulse while it is listening: the one control on the bar
            // whose state a student needs to be sure of.
            sx={
              isDictating
                ? { animation: "standePulse 1.4s ease-in-out infinite" }
                : undefined
            }
          >
            <MicIcon width="24px" height="24px" aria-hidden focusable="false" />
          </Box>
        </Tooltip>

        <Tooltip label="Submit this answer" placement="top" hasArrow fontSize="11px">
          <Flex
            as="button"
            type="button"
            aria-label="Submit this answer"
            align="center"
            justify="center"
            boxSize="29px"
            borderRadius="full"
            border="1px solid"
            borderColor={a.text}
            color={a.text}
            bg="transparent"
            flexShrink={0}
            opacity={canSubmit && !isSubmitting ? 1 : 0.4}
            cursor={canSubmit && !isSubmitting ? "pointer" : "not-allowed"}
            transition="background 0.12s, color 0.12s"
            _hover={canSubmit && !isSubmitting ? { bg: a.text, color: a.surface } : {}}
            onClick={canSubmit && !isSubmitting ? onSubmit : undefined}
          >
            <ArrowUpIcon style={{ width: 15, height: 15 }} />
          </Flex>
        </Tooltip>
      </Flex>
    </Flex>
  );
};

export default memo(AnswerActionBar);
